import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { usersAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import { Navigate } from 'react-router-dom';
import AdaptiveModal from '../../components/Common/AdaptiveModal';
import { useIsMobile } from '../../helpers/useMediaQuery';
import UserDetailModal from './UserDetailModal';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  role: { id: string; name: string; displayName: string };
  createdAt: string;
}

interface Role {
  id: string;
  name: string;
  displayName: string;
}

const UserList: React.FC = () => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    roleId: '',
  });

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([
        usersAPI.getAll(),
        usersAPI.getRoles(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        roleId: '',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.firstName || !formData.roleId) {
      toast.error('Tüm zorunlu alanları doldurun');
      return;
    }

    try {
      setSubmitting(true);
      await usersAPI.create(formData);
      toast.success('Kullanıcı oluşturuldu');
      toggleModal();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = (user: User) => {
    setSelectedUser(user);
    setDetailModalOpen(true);
  };

  const toggleDetailModal = () => {
    setDetailModalOpen(!detailModalOpen);
  };

  const filteredUsers = searchFilter
    ? users.filter((u) => {
        const s = searchFilter.toLowerCase();
        return (
          u.firstName.toLowerCase().includes(s) ||
          u.lastName.toLowerCase().includes(s) ||
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(s) ||
          u.email.toLowerCase().includes(s) ||
          u.role.displayName.toLowerCase().includes(s)
        );
      })
    : users;

  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case 'ADMIN':
        return 'danger';
      case 'MUHASEBE':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Sadece admin erişebilir
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Kullanıcı Yönetimi</h4>
            <div className="page-title-right d-flex align-items-center gap-2">
              <Input
                type="text"
                bsSize="sm"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Ad, e-posta ara..."
                style={{ width: '170px' }}
              />
              <Button color="primary" onClick={toggleModal}>
                <i className="ri-add-line me-1"></i>
                Yeni Kullanıcı
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">Kullanıcı Listesi</h5>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Henüz kullanıcı bulunmuyor.</p>
                </div>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {filteredUsers.map((user) => (
                    <div key={user.id} className="admin-card-item" onClick={() => openDetail(user)} style={{ cursor: 'pointer' }}>
                      {/* Header: Full Name + Role Badge */}
                      <div className="admin-card-header">
                        <h6 className="admin-card-title">
                          {user.firstName} {user.lastName}
                        </h6>
                        <Badge color={getRoleBadgeColor(user.role.name)} className="admin-card-badge">
                          {user.role.displayName}
                        </Badge>
                      </div>

                      {/* Body: Email, Status, Created Date */}
                      <div className="admin-card-body">
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-mail-line"></i>
                            E-posta
                          </span>
                          <span className="admin-info-value">{user.email}</span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-checkbox-circle-line"></i>
                            Durum
                          </span>
                          <span className="admin-info-value">
                            <Badge color={user.isActive ? 'success' : 'secondary'}>
                              {user.isActive ? 'Aktif' : 'Pasif'}
                            </Badge>
                          </span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-calendar-line"></i>
                            Kayıt
                          </span>
                          <span className="admin-info-value">
                            {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                      </div>

                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Ad Soyad</th>
                        <th>E-posta</th>
                        <th>Rol</th>
                        <th>Durum</th>
                        <th>Kayıt Tarihi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} onClick={() => openDetail(user)} style={{ cursor: 'pointer' }}>
                          <td>
                            <strong>
                              {user.firstName} {user.lastName}
                            </strong>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <Badge color={getRoleBadgeColor(user.role.name)}>
                              {user.role.displayName}
                            </Badge>
                          </td>
                          <td>
                            <Badge color={user.isActive ? 'success' : 'secondary'}>
                              {user.isActive ? 'Aktif' : 'Pasif'}
                            </Badge>
                          </td>
                          <td>
                            {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Yeni Kullanıcı Modal */}
      <AdaptiveModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title="Yeni Kullanıcı"
        footer={
          <>
            <Button color="secondary" onClick={toggleModal}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="user-form" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </>
        }
      >
        <Form id="user-form" onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Ad *</Label>
            <Input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="Adı girin"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Soyad *</Label>
            <Input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Soyadı girin"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>E-posta *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="ornek@alkayol.com"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Şifre *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="En az 6 karakter"
              required
              minLength={6}
            />
          </FormGroup>
          <FormGroup>
            <Label>Rol *</Label>
            <Input
              type="select"
              value={formData.roleId}
              onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
              required
            >
              <option value="">Rol Seçin</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.displayName}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Form>
      </AdaptiveModal>

      <UserDetailModal
        isOpen={detailModalOpen}
        toggle={toggleDetailModal}
        user={selectedUser}
        roles={roles}
        onUpdate={fetchData}
      />
    </>
  );
};

export default UserList;
